/**
 * Signing in. A link sent to an email address, or a Google account.
 *
 * No password is ever asked for and no phone number is collected, so there is
 * nothing on this screen for an applicant to forget or for us to store.
 */

import { useState } from "react";
import { bi } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import { Callout, ErrorNote, Panel } from "../components/ui.js";
import { StatutoryJourney } from "../components/StatutoryJourney.js";

export function SignIn({
  needEmail = false,
  onProvideEmail,
  completing = false,
  linkError = null,
}: {
  /** True where a magic link was opened on a device that does not know the address. */
  needEmail?: boolean;
  onProvideEmail?: (email: string) => Promise<void>;
  completing?: boolean;
  linkError?: string | null;
}): JSX.Element {
  const { auth } = useSession();
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (needEmail && onProvideEmail) {
        await onProvideEmail(email.trim());
      } else if (auth) {
        await auth.sendMagicLink(email.trim());
        setSent(true);
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const google = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (auth) await auth.signInWithGoogle();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (completing) {
    return (
      <Panel title={bi("Signing you in", "നിങ്ങളെ പ്രവേശിപ്പിക്കുന്നു")}>
        <p>
          <span className="spinner" aria-hidden="true" />{" "}
          {t(bi("One moment.", "ഒരു നിമിഷം."))}
        </p>
      </Panel>
    );
  }

  return (
    <div className="signin-page">
      {/* The front door. It has one job: show an applicant that the period is a
          real thing with a shape, because that is the right they cannot
          currently exercise. */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-words">
            <p className="hero-eyebrow">
              {t(bi("Kerala Act 5 of 2000", "2000-ലെ കേരള നിയമം 5"))}
            </p>
            <h1 className="hero-title">
              {t(bi("See where your application is", "നിങ്ങളുടെ അപേക്ഷ എവിടെ എത്തി എന്ന് അറിയുക"))}
            </h1>
            <p className="hero-lede">
              {t(
                bi(
                  "The law gives the Board a time limit to decide your application. This website shows you that time limit.",
                  "നിങ്ങളുടെ അപേക്ഷയിൽ തീരുമാനം എടുക്കാൻ ബോർഡിന് നിയമം ഒരു സമയപരിധി നൽകുന്നു. ആ സമയപരിധി ഈ വെബ്സൈറ്റിൽ കാണാം.",
                ),
              )}
            </p>
            <p className="hero-body">
              {t(
                bi(
                  "Find out which licences and permissions your business needs, and which office gives each one. Fill in one form instead of many separate ones. After you apply, this website shows you which Board is deciding your application, which offices have it now, how many days each office has had it, and the last day the Board has to decide.",
                  "നിങ്ങളുടെ ബിസിനസിന് ഏതൊക്കെ ലൈസൻസുകളും അനുമതികളും വേണം, ഓരോന്നും ഏത് ഓഫീസ് നൽകുന്നു എന്ന് കണ്ടെത്തുക. പല ഫോറങ്ങൾക്ക് പകരം ഒരു ഫോറം പൂരിപ്പിക്കുക. അപേക്ഷിച്ച ശേഷം, ഏത് ബോർഡ് തീരുമാനം എടുക്കുന്നു, ഇപ്പോൾ ഏതൊക്കെ ഓഫീസുകളുടെ പക്കലാണ്, ഓരോ ഓഫീസും എത്ര ദിവസമായി വച്ചിരിക്കുന്നു, ബോർഡ് തീരുമാനം എടുക്കേണ്ട അവസാന ദിവസം ഏതാണ് എന്നിവ ഈ വെബ്സൈറ്റിൽ കാണാം.",
                ),
              )}
            </p>
          </div>
          <div className="hero-figure">
            <StatutoryJourney lang={lang} />
            <p className="hero-figcaption">
              {t(
                bi(
                  "These are the steps the Rules ask the Board to follow. Day 30 is the last day the Board has to decide.",
                  "ബോർഡ് പിന്തുടരേണ്ട ഘട്ടങ്ങളാണ് ഇവ. ബോർഡ് തീരുമാനം എടുക്കേണ്ട അവസാന ദിവസം 30-ാം ദിവസമാണ്.",
                ),
              )}
            </p>
          </div>
        </div>
      </section>

      <div className="signin-body">
      {linkError ? (
        <Callout tone="alarm" title={bi("That link did not work", "ആ ലിങ്ക് പ്രവർത്തിച്ചില്ല")}>
          <p style={{ marginBottom: 0 }}>
            {t(
              bi(
                "The link may have been used already, or it may have expired. Ask for a new one below.",
                "ആ ലിങ്ക് ഇതിനകം ഉപയോഗിച്ചിരിക്കാം, അല്ലെങ്കിൽ കാലഹരണപ്പെട്ടിരിക്കാം. താഴെ പുതിയൊരെണ്ണം ആവശ്യപ്പെടുക.",
              ),
            )}
          </p>
        </Callout>
      ) : null}

      {error ? <ErrorNote error={error} /> : null}

      {sent ? (
        <Callout
          tone="calm"
          title={bi("Check your email", "നിങ്ങളുടെ ഇമെയിൽ പരിശോധിക്കുക")}
        >
          <p style={{ marginBottom: 0 }}>
            {t(
              bi(
                `We have sent a link to ${email}. Open it on this device and you will be signed in. The link works once.`,
                `${email} എന്ന വിലാസത്തിലേക്ക് ഒരു ലിങ്ക് അയച്ചിട്ടുണ്ട്. ഈ ഉപകരണത്തിൽ അത് തുറന്നാൽ നിങ്ങൾ പ്രവേശിക്കും. ലിങ്ക് ഒരു തവണ മാത്രമേ പ്രവർത്തിക്കൂ.`,
              ),
            )}
          </p>
        </Callout>
      ) : (
        <Panel
          title={
            needEmail
              ? bi("Confirm your email address", "നിങ്ങളുടെ ഇമെയിൽ വിലാസം സ്ഥിരീകരിക്കുക")
              : bi("Sign in with a link", "ഒരു ലിങ്ക് വഴി പ്രവേശിക്കുക")
          }
        >
          <p>
            {needEmail
              ? t(
                  bi(
                    "You opened the link on a different device from the one that asked for it. Type the address you asked for it with, and we will finish signing you in.",
                    "ലിങ്ക് ആവശ്യപ്പെട്ട ഉപകരണത്തിൽ നിന്ന് വ്യത്യസ്തമായ ഒരു ഉപകരണത്തിലാണ് നിങ്ങൾ അത് തുറന്നത്. ഏത് വിലാസത്തിലാണ് ആവശ്യപ്പെട്ടത് എന്ന് ടൈപ്പ് ചെയ്യുക, പ്രവേശനം പൂർത്തിയാക്കാം.",
                  ),
                )
              : t(
                  bi(
                    "Give an email address and we will send a link to it. There is no password to remember.",
                    "ഒരു ഇമെയിൽ വിലാസം നൽകുക, അതിലേക്ക് ഒരു ലിങ്ക് അയയ്ക്കാം. ഓർത്തുവയ്ക്കാൻ പാസ്‌വേഡ് ഒന്നുമില്ല.",
                  ),
                )}
          </p>

          <div className="field">
            <label className="field-label" htmlFor="signin-email">
              {t(bi("Email address", "ഇമെയിൽ വിലാസം"))}
            </label>
            <input
              id="signin-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              placeholder="name@example.com"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email.includes("@")) void send();
              }}
            />
          </div>

          <button
            type="button"
            className="btn"
            onClick={() => void send()}
            disabled={busy || !email.includes("@")}
          >
            {busy ? <span className="spinner" aria-hidden="true" /> : null}
            {needEmail
              ? t(bi("Finish signing in", "പ്രവേശനം പൂർത്തിയാക്കുക"))
              : t(bi("Send me a link", "എനിക്ക് ഒരു ലിങ്ക് അയയ്ക്കുക"))}
          </button>

          <hr className="rule" />

          <p className="muted">{t(bi("Or", "അല്ലെങ്കിൽ"))}</p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void google()}
            disabled={busy}
          >
            {t(bi("Continue with Google", "ഗൂഗിൾ ഉപയോഗിച്ച് തുടരുക"))}
          </button>
        </Panel>
      )}

        <p className="muted signin-note">
          {t(
            bi(
              "We ask for nothing the state already holds about you, and we never ask for a password.",
              "സർക്കാരിന്റെ പക്കൽ ഇതിനകം ഉള്ള വിവരങ്ങൾ ഞങ്ങൾ വീണ്ടും ചോദിക്കില്ല; പാസ്‌വേഡ് ഒരിക്കലും ചോദിക്കില്ല.",
            ),
          )}
        </p>
      </div>
    </div>
  );
}
